import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AllCarts.module.css";
import { API } from "../../config";
import axios from "axios";
import Spin from "../Spinner/Spinner";
import { SearchInput } from "../ProductListing/Filters";

const LIMIT = 8;

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const AllCarts = () => {
  const navigate = useNavigate();
  const [carts, setCarts] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCarts: 0,
    limit: LIMIT,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getCarts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/Admin/carts`, {
        params: {
          page,
          limit: LIMIT,
          search: debouncedSearch || undefined,
          sortBy,
          sortOrder,
        },
      });
      setCarts(response.data.carts || []);
      setPagination(response.data.pagination || pagination);
    } catch (err) {
      console.error("Failed to fetch carts:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCarts();
  }, [page, debouncedSearch, sortBy, sortOrder]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSortChange = (e) => {
    const value = e.target.value;
    if (value === "createdAt_asc") {
      setSortBy("createdAt");
      setSortOrder("asc");
    } else if (value === "createdAt_desc") {
      setSortBy("createdAt");
      setSortOrder("desc");
    } else if (value === "updatedAt_asc") {
      setSortBy("updatedAt");
      setSortOrder("asc");
    } else if (value === "updatedAt_desc") {
      setSortBy("updatedAt");
      setSortOrder("desc");
    }
  };

  const sortValue = `${sortBy}_${sortOrder}`;

  if (loading && carts.length === 0) {
    return <Spin />;
  }
  if (error) {
    return <div>Unable to get carts</div>;
  }

  return (
    <div className={styles.container}>
      <h1>Active Carts</h1>
      <div className={styles.searchContainer}>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by cart name, username, company and email"
        />
        <select
          className={styles.sortSelect}
          value={sortValue}
          onChange={handleSortChange}
          aria-label="Sort carts"
        >
          <option value="createdAt_desc">Created At (Newest first)</option>
          <option value="createdAt_asc">Created At (Oldest first)</option>
          <option value="updatedAt_desc">Updated At (Newest first)</option>
          <option value="updatedAt_asc">Updated At (Oldest first)</option>
        </select>
      </div>
      <div className={styles.cartItemsContainer}>
        {carts.length === 0 ? (
          <p className={styles.noCarts}>No carts found</p>
        ) : (
          carts.map((cart) => (
            <CartCard
              key={cart._id}
              cart={cart}
              onViewDetails={() =>
                navigate(`/users/${cart.userId}/carts/${cart._id}`)
              }
            />
          ))
        )}
      </div>
      {pagination.totalPages > 1 && (
        <div className={styles.paginationContainer}>
          <button
            type="button"
            className={styles.pageButton}
            disabled={page <= 1}
            onClick={() => page > 1 && setPage(page - 1)}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {pagination.currentPage || page} of {pagination.totalPages}
          </span>
          <button
            type="button"
            className={styles.pageButton}
            disabled={page >= pagination.totalPages}
            onClick={() => page < pagination.totalPages && setPage(page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const CartCard = ({ cart, onViewDetails }) => {
  const formatPrice = (price) => {
    if (price == null || price === undefined) return "-";
    const rounded = Math.round(price);
    return `₹${rounded.toLocaleString("en-IN")}/-`;
  };

  const data = [
    { title: "Name", value: cart.name ?? "-" },
    { title: "Company", value: cart.companyName ?? "-" },
    { title: "Created By", value: cart.createdByName ?? "-" },
    { title: "Total Products", value: cart.totalProducts ?? 0 },
    { title: "Price", value: formatPrice(cart.price) },
    { title: "Created At", value: formatDate(cart.createdAt) },
    { title: "Updated At", value: formatDate(cart.updatedAt) },
  ];

  return (
    <div className={styles.cartCard}>
      <div className={styles.textContainer}>
        <div className={styles.text}>
          {data.map((item) => (
            <div key={item.title} className={styles.textCell}>
              <h4 className={styles.textLabel}>{item.title}</h4>
              <h1 className={styles.textValue}>{item.value || "-"}</h1>
            </div>
          ))}
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.textualButton}
            onClick={onViewDetails}
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};
